import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import I18n from 'I18n';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UIFlex from 'UIComponents/layout/UIFlex';
import UITable from 'UIComponents/table/UITable';
import styled, { theme } from '../../../utils/Styling';
const TableContext = /*#__PURE__*/createContext({
  selectedRows: [],
  totalRows: 0,
  toggleRowSelection: () => {},
  toggleSelectAll: () => {}
});
export const TableProvider = ({
  children,
  ids
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const toggleSelectAll = useCallback(() => {
    const selectAll = selectedRows.length === 0;
    setSelectedRows(selectAll ? ids : []);
  }, [ids, selectedRows.length]);
  const toggleRowSelection = useCallback(id => {
    setSelectedRows(rows => rows.includes(id) ? rows.filter(value => value !== id) : [...rows, id]);
  }, []);
  const contextState = useMemo(() => ({
    selectedRows,
    totalRows: ids.length,
    toggleSelectAll,
    toggleRowSelection
  }), [selectedRows, ids.length, toggleSelectAll, toggleRowSelection]);
  return /*#__PURE__*/_jsx(TableContext.Provider, {
    value: contextState,
    children: children
  });
};
const CHECKBOX_COLUMN_WIDTH = 60;
const StyledTableHeader = styled.tr.withConfig({
  displayName: "Table__StyledTableHeader",
  componentId: "sc-1ji2cwi-0"
})(["background:", ";position:relative;th:not(.fixed-header){text-transform:none !important;visibility:", ";}"], theme.colors.GYPSUM, ({
  $showActions
}) => $showActions ? 'hidden' : 'visible');
const HeaderActions = styled.tr.withConfig({
  displayName: "Table__HeaderActions",
  componentId: "sc-1ji2cwi-1"
})(["position:absolute;padding:0;background:none;padding-left:24px;height:100%;"]);

const TableHeader = ({
  renderHeader,
  renderHeaderActions
}) => {
  const {
    selectedRows,
    toggleSelectAll,
    totalRows
  } = useContext(TableContext);
  const showActions = selectedRows.length > 0;
  return /*#__PURE__*/_jsxs(StyledTableHeader, {
    $showActions: showActions ? 1 : 0,
    children: [/*#__PURE__*/_jsx("th", {
      className: "fixed-header",
      children: /*#__PURE__*/_jsx(UICheckbox, {
        "aria-label": I18n.text('learningCenter.table.checkboxAllTip'),
        checked: selectedRows.length === totalRows,
        indeterminate: selectedRows.length > 0,
        onChange: toggleSelectAll
      })
    }), /*#__PURE__*/_jsx(HeaderActions, {
      className: "fixed-header",
      children: /*#__PURE__*/_jsx(UIFlex, {
        align: "center",
        style: {
          height: '100%'
        },
        children: showActions && renderHeaderActions(selectedRows.length)
      })
    }), renderHeader()]
  });
};

const TableRow = ({
  rowId,
  row,
  renderRow
}) => {
  const {
    selectedRows,
    toggleRowSelection
  } = useContext(TableContext);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsx(UICheckbox, {
        "aria-label": I18n.text('learningCenter.table.checkboxTip'),
        checked: selectedRows.includes(rowId),
        onChange: () => toggleRowSelection(rowId)
      })
    }), /*#__PURE__*/_jsx("td", {
      style: {
        padding: 0
      }
    }), renderRow(row)]
  });
};

const Table = ({
  rows,
  renderHeader,
  renderHeaderActions,
  renderRow
}) => {
  return /*#__PURE__*/_jsx(TableProvider, {
    ids: Object.keys(rows),
    children: /*#__PURE__*/_jsxs(UITable, {
      condensed: true,
      children: [/*#__PURE__*/_jsx("colgroup", {
        children: /*#__PURE__*/_jsx("col", {
          style: {
            width: CHECKBOX_COLUMN_WIDTH
          }
        })
      }), /*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsx(TableHeader, {
          renderHeader: renderHeader,
          renderHeaderActions: renderHeaderActions,
          rows: rows
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: Object.keys(rows).map(id => /*#__PURE__*/_jsx("tr", {
          children: /*#__PURE__*/_jsx(TableRow, {
            renderRow: renderRow,
            row: rows[id],
            rowId: id
          })
        }, id))
      })]
    })
  });
};

export default Table;